/* ==========================================================================
   Mega menu / super nav — Hype Wood Co
   Desktop only (>= 990px). Opens on hover intent, click, or keyboard focus.
   ========================================================================== */

class MegaMenu extends HTMLElement {
  constructor() {
    super();
    this.openDelay = 90;
    this.closeDelay = 200;
    this.openTimer = null;
    this.closeTimer = null;
    this.activeKey = null;

    this.onDocKeydown = this.onDocKeydown.bind(this);
    this.onDocPointerDown = this.onDocPointerDown.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
  }

  static handleize(value) {
    const source = (value || '').normalize('NFD');
    let out = '';
    for (let i = 0; i < source.length; i++) {
      const code = source.charCodeAt(i);
      if (code >= 768 && code <= 879) continue;
      const ch = source[i].toLowerCase();
      const ok = (ch >= 'a' && ch <= 'z') || (ch >= '0' && ch <= '9');
      out += ok ? ch : '-';
    }
    while (out.indexOf('--') !== -1) out = out.replace('--', '-');
    while (out.charAt(0) === '-') out = out.slice(1);
    while (out.charAt(out.length - 1) === '-') out = out.slice(0, -1);
    return out;
  }

  connectedCallback() {
    const host = document.querySelector('.site-header');
    if (host && this.parentElement !== host) {
      host.appendChild(this);
      return;
    }

    this.header = this.closest('.site-header') || document.querySelector('.site-header');
    this.panels = new Map();
    this.querySelectorAll('[data-mega-panel]').forEach((panel) => {
      this.panels.set(panel.dataset.megaPanel, panel);
    });

    this.triggers = [];
    if (this.header) {
      this.header.querySelectorAll('[data-mega-trigger]').forEach((trigger) => {
        if (this.panels.has(trigger.dataset.megaTrigger)) this.triggers.push(trigger);
      });
    }

      if (!this.triggers.length && this.header) {
        this.header.querySelectorAll('.site-header__nav a').forEach((link) => {
          const key = MegaMenu.handleize(link.textContent);
          if (this.panels.has(key)) {
            link.dataset.megaTrigger = key;
            this.triggers.push(link);
          }
        });
      }

    this.setupMobile();

    if (!this.panels.size || !this.triggers.length) return;

    this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.desktop = window.matchMedia('(min-width: 990px)');

    this.bind();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onDocKeydown);
    document.removeEventListener('pointerdown', this.onDocPointerDown);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
  }

  bind() {
    this.triggers.forEach((trigger) => {
      const key = trigger.dataset.megaTrigger;
      const panel = this.panels.get(key);

      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-expanded', 'false');
      if (panel.id) trigger.setAttribute('aria-controls', panel.id);

      if (this.canHover) {
        trigger.addEventListener('mouseenter', () => this.scheduleOpen(key));
        trigger.addEventListener('mouseleave', () => this.scheduleClose());
      }

      // Click opens the panel the first time; a second click follows the link.
      trigger.addEventListener('click', (event) => {
        if (!this.isDesktop()) return;
        if (this.activeKey !== key) {
          event.preventDefault();
          this.open(key);
        }
      });

      // Keyboard users tabbing onto the item get the panel too.
      trigger.addEventListener('focus', () => {
        if (this.isDesktop() && this.usingKeyboard) this.open(key);
      });
    });

    this.panels.forEach((panel) => {
      if (this.canHover) {
        panel.addEventListener('mouseenter', () => this.cancelClose());
        panel.addEventListener('mouseleave', () => this.scheduleClose());
      }
      panel.addEventListener('focusout', (event) => {
        if (!panel.contains(event.relatedTarget) && !this.isTrigger(event.relatedTarget)) {
          this.close();
        }
      });
    });

    document.addEventListener('keydown', this.onDocKeydown);
    document.addEventListener('pointerdown', this.onDocPointerDown);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);

    // Track whether focus is arriving from the keyboard, so a mouse click
    // on a nav item does not fight with the focus handler.
    this.usingKeyboard = false;
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Tab') this.usingKeyboard = true;
    });
    document.addEventListener('pointerdown', () => {
      this.usingKeyboard = false;
    });
  }

  /* Keep the scrim below the header bar so the nav keeps its own colour. */
  setScrimOffset() {
    const bar = this.header || document.querySelector('.site-header');
    if (!bar) return;
    const bottom = Math.max(0, Math.round(bar.getBoundingClientRect().bottom));
    this.style.setProperty('--mega-scrim-top', bottom + 'px');
  }

  /* Mirror each panel into the mobile drawer as a text-only accordion. */
  setupMobile() {
    const drawer = document.querySelector('[data-mobile-menu] .mobile-menu__nav');
    if (!drawer) return;

    const lists = new Map();
    this.querySelectorAll('[data-mega-mobile]').forEach((list) => {
      lists.set(list.dataset.megaMobile, list);
    });
    if (!lists.size) return;

    drawer.querySelectorAll('.mobile-menu__group').forEach((group, index) => {
      if (group.querySelector('[data-mobile-accordion-trigger]')) return;

      const link = group.querySelector('.mobile-menu__section-link');
      if (!link) return;

      const key = MegaMenu.handleize(link.textContent);
      const list = lists.get(key);
      if (!list) return;

      const panelId = 'MobileMegaPanel-' + key + '-' + index;

      const panel = document.createElement('div');
      panel.className = 'mobile-menu__section-panel';
      panel.id = panelId;
      panel.hidden = true;
      list.querySelectorAll('a').forEach((source) => {
        const item = document.createElement('a');
        item.className = 'mobile-menu__standalone-link';
        item.href = source.getAttribute('href') || '#';
        item.textContent = source.textContent.trim();
        panel.appendChild(item);
      });
      if (!panel.children.length) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'mobile-menu__section-toggle ui-heading';
      button.setAttribute('data-mobile-accordion-trigger', '');
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', panelId);
      const label = document.createElement('span');
      label.textContent = link.textContent.trim();
      const icon = document.createElement('span');
      icon.className = 'mobile-menu__toggle-icon';
      icon.setAttribute('aria-hidden', 'true');
      button.appendChild(label);
      button.appendChild(icon);

      group.replaceChild(button, link);
      group.appendChild(panel);
    });
  }

  isDesktop() {
    return this.desktop.matches;
  }

  isTrigger(el) {
    return !!el && this.triggers.indexOf(el) !== -1;
  }

  scheduleOpen(key) {
    if (!this.isDesktop()) return;
    this.cancelClose();
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => this.open(key), this.activeKey ? 0 : this.openDelay);
  }

  scheduleClose() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.close(), this.closeDelay);
  }

  cancelClose() {
    clearTimeout(this.closeTimer);
  }

  open(key) {
    if (!this.panels.has(key) || this.activeKey === key) return;
    this.cancelClose();
    clearTimeout(this.openTimer);

    this.panels.forEach((panel, panelKey) => {
      panel.classList.toggle('is-open', panelKey === key);
    });
    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(trigger.dataset.megaTrigger === key));
    });

    this.activeKey = key;
    this.scrollAtOpen = window.scrollY;
    this.setScrimOffset();
    this.classList.add('is-open');
    if (this.header) this.header.classList.add('site-header--mega-open');
    this.dispatchEvent(new CustomEvent('mega-menu:open', { bubbles: true, detail: { key } }));
  }

  close() {
    if (!this.activeKey) return;
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);

    this.panels.forEach((panel) => panel.classList.remove('is-open'));
    this.triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));

    this.activeKey = null;
    this.classList.remove('is-open');
    if (this.header) this.header.classList.remove('site-header--mega-open');
    this.dispatchEvent(new CustomEvent('mega-menu:close', { bubbles: true }));
  }

  onDocKeydown(event) {
    if (event.key !== 'Escape' || !this.activeKey) return;
    const key = this.activeKey;
    this.close();
    const trigger = this.triggers.find((item) => item.dataset.megaTrigger === key);
    if (trigger) trigger.focus();
  }

  onDocPointerDown(event) {
    if (!this.activeKey) return;
    if (this.contains(event.target)) return;
    if (this.isTrigger(event.target) || (event.target.closest && event.target.closest('[data-mega-trigger]'))) return;
    this.close();
  }

  onScroll() {
    if (!this.activeKey) return;
    if (Math.abs(window.scrollY - (this.scrollAtOpen || 0)) > 40) this.close();
  }

  onResize() {
    if (!this.isDesktop()) this.close();
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu);
}

/* Theme editor: keep the right panel open while a card block is selected. */
document.addEventListener('shopify:block:select', (event) => {
  const panel = event.target.closest('[data-mega-panel]');
  const root = event.target.closest('mega-menu');
  if (panel && root) {
    root.open(panel.dataset.megaPanel);
    root.cancelClose();
  }
});

document.addEventListener('shopify:block:deselect', (event) => {
  const root = event.target.closest('mega-menu');
  if (root) root.close();
});